import { useState, useEffect, useCallback, useRef } from 'react';
import { generateClient, GraphQLResult, GraphQLSubscription } from 'aws-amplify/api';
import { Scan } from '../types';
import { LIST_SCANS } from '../graphql/queries';
import { ON_SITE_SCANS_UPDATED } from '../graphql/subscriptions';

const client = generateClient();

interface ListScansResponse {
  listScans: { items: Scan[] };
}

interface ScanSubscriptionData {
  onSiteScansUpdated: Scan;
}

export function useScans(siteId: string, options: { includeTrash?: boolean; enableSubscription?: boolean } = {}) {
  const { includeTrash = false, enableSubscription = true } = options;
  const [scans, setScans] = useState<Scan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  // Scans archived to trash this session. The subscription payload for a scan
  // (e.g. a late cancelScan event) has no `trashedAt`, so without this the
  // handler below would blindly re-add a scan the user just trashed.
  const trashedIdsRef = useRef<Set<string>>(new Set());

  // `silent` skips the loading/error UI so background reconciles (tab focus,
  // subscription-driven refresh) update the list in place without flashing a loader.
  const fetchScans = useCallback(async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await client.graphql({
        query: LIST_SCANS,
        variables: { siteId, includeTrash },
      }) as GraphQLResult<ListScansResponse>;

      setScans(response.data?.listScans?.items || []);
    } catch (err) {
      console.error('Error fetching scans:', err);
      if (!silent) {
        setError('Failed to load scans.');

        // Mock data for development
        if (import.meta.env.DEV) {
          setScans(getMockScans(siteId));
        }
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [siteId, includeTrash]);

  // (Re)establish the real-time scan subscription. Tears down any existing
  // subscription first so this is safe to call repeatedly (e.g. on tab focus).
  const setupSubscription = useCallback(() => {
    if (!enableSubscription || !siteId) return;

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }

    try {
      const subscription = client.graphql<GraphQLSubscription<ScanSubscriptionData>>({
        query: ON_SITE_SCANS_UPDATED,
        variables: { siteId },
      });

      subscriptionRef.current = subscription.subscribe({
        next: ({ data }) => {
          if (data?.onSiteScansUpdated) {
            const updatedScan = data.onSiteScansUpdated;
            // Ignore events for scans the user archived to trash — they belong
            // in the Trash view now, not Scan History.
            if (trashedIdsRef.current.has(updatedScan.scanId)) {
              return;
            }
            // If the event itself says the scan is trashed, drop it from the
            // list and remember it — so a later event without `trashedAt` can't
            // re-add it, and it survives a reload's fresh (trash-excluding) fetch.
            if (updatedScan.trashedAt) {
              trashedIdsRef.current.add(updatedScan.scanId);
              setScans(prevScans => prevScans.filter(s => s.scanId !== updatedScan.scanId));
              return;
            }
            // Subscription payload doesn't include `results` (overallScore,
            // totalIssues, etc.). When a scan transitions to COMPLETED we
            // need that data to render View Results + severity breakdown,
            // so refetch listScans to get the full shape.
            // Backend sends ScanStatus in uppercase ('COMPLETED').
            if (updatedScan.status?.toLowerCase() === 'completed') {
              fetchScans(true);
              return;
            }
            setScans(prevScans => {
              // Check if this scan already exists
              const existingIndex = prevScans.findIndex(s => s.scanId === updatedScan.scanId);
              if (existingIndex >= 0) {
                // Update existing scan
                const newScans = [...prevScans];
                newScans[existingIndex] = updatedScan;
                return newScans;
              } else {
                // Add new scan to the beginning
                return [updatedScan, ...prevScans];
              }
            });
          }
        },
        error: (err) => {
          console.error('Subscription error:', err);
        },
      });
    } catch (err) {
      console.error('Failed to setup subscription:', err);
    }
  }, [siteId, enableSubscription, fetchScans]);

  // Subscribe to real-time scan updates on mount / when the site changes
  useEffect(() => {
    setupSubscription();

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [setupSubscription]);

  // The AppSync WebSocket does not survive laptop sleep / tab backgrounding.
  // Events fired while disconnected are lost AND the dead socket never resumes
  // on its own, so an in-progress scan would stay frozen on its last seen state.
  // On return: refetch to reconcile missed events, then re-establish the
  // subscription so live updates continue.
  useEffect(() => {
    if (!enableSubscription || !siteId) return;

    const reconcile = () => {
      if (document.visibilityState === 'visible') {
        fetchScans(true);
        setupSubscription();
      }
    };

    document.addEventListener('visibilitychange', reconcile);
    window.addEventListener('focus', reconcile);

    return () => {
      document.removeEventListener('visibilitychange', reconcile);
      window.removeEventListener('focus', reconcile);
    };
  }, [enableSubscription, siteId, fetchScans, setupSubscription]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  // Remove a scan from local state immediately (e.g. after archiving to trash),
  // so the card disappears without waiting for the refetch — and so a stale
  // subscription event can't leave a just-trashed scan lingering in the list.
  const removeScan = useCallback((scanId: string) => {
    trashedIdsRef.current.add(scanId);
    setScans(prev => prev.filter(s => s.scanId !== scanId));
  }, []);

  return {
    scans,
    isLoading,
    error,
    refetch: fetchScans,
    removeScan,
  };
}

function getMockScans(siteId: string): Scan[] {
  return [
    {
      siteId,
      scanId: 'scan-1',
      userId: 'user-1',
      status: 'completed',
      progress: {
        phase: 'completed',
        pagesDiscovered: 45,
        pagesScanned: 45,
      },
      config: {
        siteUrl: 'https://example.com',
        scanTypes: ['accessibility', 'eaa', 'seo', 'performance', 'brokenLinks'],
        wcagVersion: '2.2',
        wcagLevel: 'AA',
        pageLimit: 100,
        rateLimit: 'gentle',
      },
      results: {
        overallScore: 78,
        overallGrade: 'C',
        totalIssues: {
          critical: 3,
          serious: 12,
          moderate: 28,
          minor: 45,
          total: 88,
        },
      },
      startedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      completedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 15 * 60 * 1000).toISOString(),
    },
    {
      siteId,
      scanId: 'scan-2',
      userId: 'user-1',
      status: 'completed',
      progress: {
        phase: 'completed',
        pagesDiscovered: 42,
        pagesScanned: 42,
      },
      config: {
        siteUrl: 'https://example.com',
        scanTypes: ['accessibility', 'eaa'],
        wcagVersion: '2.1',
        wcagLevel: 'AA',
        pageLimit: 50,
        rateLimit: 'gentle',
      },
      results: {
        overallScore: 72,
        overallGrade: 'C',
        totalIssues: {
          critical: 5,
          serious: 15,
          moderate: 32,
          minor: 50,
          total: 102,
        },
      },
      startedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
      completedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000 + 12 * 60 * 1000).toISOString(),
    },
  ];
}
